import { useEffect, useRef } from 'react';
import { CreateGnbWidgetProps, GnbWidgetInstance, PHASE_TYPE } from '@kakao/biz-gnb';

import * as Sentry from '@sentry/react';

import { getUncheckedCount } from '@/api/lounge-board/bulletin/uncheckedCount';
import { getMyInfo } from '@/api/pc-api/v1/user/my';
import { ROUTES } from '@/constants/routes';
import { KAKAO_BUSINESS_CS_CENTER } from '@/constants/url';
import { useAuthStore } from '@/store/auth';
import { useUserStore } from '@/store/user';

type GNBPropsParams = {
  element: HTMLHeadElement;
  name?: string;
  email?: string;
  isNew: boolean;
  targetId: number | null;
  targetType: string | null;
};

const getGNBProps = ({ element, name, email, isNew, targetId, targetType }: GNBPropsParams): CreateGnbWidgetProps => {
  const searchParams = new URLSearchParams({ serviceType: 'KAKAOWALLET' });
  targetId && searchParams.append('targetId', `${targetId}`);
  targetType && searchParams.append('targetType', targetType);
  const loungeBoardUrl = `${KAKAO_BUSINESS_LOUNGE_BOARD_URL}/post/list?${searchParams.toString()}`;

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const phase = {
    sandbox: PHASE_TYPE.SANDBOX,
    cbt: PHASE_TYPE.CBT,
    production: PHASE_TYPE.PROD
  }[PHASE]!;

  return {
    element,
    name,
    email,
    phase,
    serviceInfo: {
      imageUrl: `${MARKUP_IMAGE_TENTH_URL}/logo_wallet.png`,
      landingUrl: ROUTES.ROOT,
      serviceName: '지갑파트너센터'
    },
    customMyInfoLink: {
      href: ROUTES.ACCOUNT.INDEX
    },
    customServiceMenuList: [
      {
        link: {
          href: `${KAKAO_BUSINESS_LOUNGE_BOARD_URL}/bulletin/list?serviceType=KAKAOWALLET`,
          label: '공지사항',
          target: '_blank' as const
        },
        isDisplayNewBadge: isNew
      },
      {
        link: {
          href: loungeBoardUrl,
          label: `문의게시판`,
          target: '_blank' as const
        }
      },
      {
        link: {
          href: KAKAO_BUSINESS_CS_CENTER,
          label: '고객센터',
          target: '_blank' as const
        }
      }
    ],
    onError: (error) => {
      if (error.type === 'boundary') {
        Sentry.captureException(error);
      }
    }
  };
};

const GNBLayout = () => {
  const setUserInfo = useUserStore((state) => state.setUserInfo);
  const gnb = useRef<GnbWidgetInstance | null>(null);
  const headerRef = useRef<HTMLHeadElement>(null);

  const deleteMyMenu = (header: HTMLHeadElement) => {
    const applyMenuStyle = (shadowRoot: ShadowRoot) => {
      const stylesheet = new CSSStyleSheet();
      stylesheet.insertRule('.gnb_relate { display: none; }');
      shadowRoot.adoptedStyleSheets.push(stylesheet);
    };

    const waitForShadowRoot = (callback: (shadowRoot: ShadowRoot) => void) => {
      const shadowRoot = header.firstElementChild?.shadowRoot;
      if (shadowRoot) {
        callback(shadowRoot);
        return;
      }

      const observer = new MutationObserver(() => {
        const shadowRoot = header.firstElementChild?.shadowRoot;
        if (shadowRoot) {
          callback(shadowRoot);
          observer.disconnect();
        }
      });
      observer.observe(header, { childList: true, subtree: true });
    };

    waitForShadowRoot(applyMenuStyle);
  };

  useEffect(() => {
    let isUnmounted = false as boolean;
    let unsubscribe = () => {};

    (async () => {
      const [myInfoSettled, countSettled, bizGnbSettled] = await Promise.allSettled([
        getMyInfo(),
        getUncheckedCount(),
        import('@kakao/biz-gnb')
      ]);
      if (!headerRef.current || isUnmounted || bizGnbSettled.status !== 'fulfilled') return;
      const element = headerRef.current;

      const myInfo = myInfoSettled.status === 'fulfilled' ? myInfoSettled.value.data.data : undefined;
      const count = countSettled.status === 'fulfilled' ? (countSettled.value?.data.count ?? 0) : 0;
      const { createGnbWidget } = bizGnbSettled.value;

      // 서버 점검중에는 반환값이 없을 수 있다.
      const isMaintenance = myInfo === undefined;
      const { id, name, email, phoneNumber } = isMaintenance
        ? { id: '', name: undefined, email: undefined, phoneNumber: '010-0000-0000' }
        : myInfo;
      const isNew = count > 0;

      const { targetId, targetType } = useAuthStore.getState();
      const initialProps = getGNBProps({ element, name, email, isNew, targetId, targetType });
      gnb.current = createGnbWidget(initialProps);
      setUserInfo({ id, name: name ?? '', email: email ?? '', phoneNumber });

      isMaintenance && deleteMyMenu(element);

      unsubscribe = useAuthStore.subscribe(({ targetId, targetType }, prevState) => {
        if (targetId === prevState.targetId && targetType === prevState.targetType) return;
        gnb.current?.update(getGNBProps({ element, name, email, isNew, targetId, targetType }));
      });
    })();

    return () => {
      isUnmounted = true;
      gnb.current?.destroy();
      gnb.current = null;
      unsubscribe();
    };
  }, []);

  return <header ref={headerRef} id="kakaoHead" />;
};

export default GNBLayout;
